import { useTranslation } from 'react-i18next';
import { useNewBlockAddedHandler } from 'components/editor/hooks/useNewBlockAddedHandler.ts';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';
import { GridBlockType } from 'services/entities/BlockTypes';
import { openNotification } from 'components/notification/index.tsx';
import { ErrorCode } from '../../../services/socket/SocketEvents.tsx';

import { useBlockContentObjectChangedHandler } from 'components/editor/hooks/useBlockContentObjectChangedHandler.ts';
import type { VideoContent } from 'components/editor/videos/video.ts';
import type { BlockSettings } from '../grid/reduxStore/blockStyleSettingsSlice.ts';
import type { TableType } from '../grid/reduxStore/table.types.ts';
import { useOpenBlockSettings } from '../GridDndEditor/BlockSettings/useOpenBlockSettings.ts';
import type { Block } from '../GridDndEditor/models/Block.model.ts';
import { useBlockStyleSettings } from './useBlockSettings.ts';
import { type EmbedContent } from '../embed/embed.ts';

type CreateBlockWithSettingsProps = {
  itemType: GridBlockType;
  blockConfig: Block;
  sectionId?: string;
  content?: string | TableType | VideoContent | EmbedContent | null;
  blockStyleSettings: BlockSettings;
};

export const useCreateBlockWithSettings = () => {
  const { t } = useTranslation();
  const blockNewAddedHandler = useNewBlockAddedHandler();
  const { saveCopiedBlockStyle } = useBlockStyleSettings();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();
  const { openBlockSettings, openTableDefaultSettings } = useOpenBlockSettings();
  const contentObjectChanged = useBlockContentObjectChangedHandler();
  return async ({ itemType, blockConfig, sectionId, content, blockStyleSettings }: CreateBlockWithSettingsProps) => {
    const isVideoBlock = itemType === GridBlockType.VIDEO;
    let newBlockContent: Record<string, any> = { styles: blockStyleSettings };
    if (isVideoBlock) {
      newBlockContent = { ...newBlockContent, htmlContent: '' };
    } else if (itemType === GridBlockType.TABLE) {
      newBlockContent = { ...newBlockContent, content };
    } else {
      newBlockContent = { ...newBlockContent, htmlContent: content };
    }
    const response = await blockNewAddedHandler(sectionId || '', newBlockContent, blockConfig, itemType);

    if (checkDocumentLockStatus(response.errorCode)) {
      return null;
    }

    if (response.errorCode === ErrorCode.VALIDATION_ERROR || response.errorCode === ErrorCode.INTERNAL_ERROR) {
      openNotification({
        type: 'warning',
        title: t('document.grid.copy_paste.warning.block_action_failed.title'),
        description: t('document.grid.copy_paste.warning.block_action_failed.description'),
      });
      return null;
    }

    const newCopiedBlockId = response.content.blockId;

    if (itemType === GridBlockType.TABLE) {
      openTableDefaultSettings(newCopiedBlockId);
    } else if (isVideoBlock && typeof content === 'object') {
      const videoContent = content || ({} as VideoContent);
      const videoSectionId = sectionId || '';

      await contentObjectChanged(newCopiedBlockId, videoSectionId, videoContent);
      openBlockSettings(itemType, newCopiedBlockId);
    } else {
      openBlockSettings(itemType, newCopiedBlockId);
    }
    await saveCopiedBlockStyle(newCopiedBlockId, blockStyleSettings);
    return newCopiedBlockId;
  };
};
